import {
  AiSlimBanner,
  Css,
  RightPaneBanner,
  RightPanePanel,
  RightPanePanelProps,
  useComputed,
  useModal,
  useRightPaneActions,
  useSnackbar,
} from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useEffect, useMemo } from "react";
import {
  aiProposalsConfig,
  entityIsRemoved,
  flattenEntityProposals,
} from "src/components/formFields/ai/utils/aiProposalForm";
import { AiFormAepFragment } from "src/components/formFields/ai/utils/aiProposalForm.generated";
import { findAiFieldProposal } from "src/components/formFields/ai/utils/bindAiProposal";
import { resolvePendingAiProposals } from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { AiEntityProposalType, AiFieldProposalStatus } from "src/generated/graphqlTypes";
import {
  lineTypeDetails,
  lineTypeOf,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/lineTypes";
import {
  SingleLinePanelMode,
  useTakeoffsSidePanel,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/useTakeoffsSidePanel";
import {
  toTliInput,
  useDeleteTakeoffLines,
  useResolveTakeoffProposals,
  useTakeoffProposals,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/useTakeoffProposals";
import {
  AddEditItemModal_TliFragment,
  useAddEditItemModal_GetTliQuery,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditItemModal.generated";
import { LaborFields } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/LaborFields";
import { MaterialFields } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/MaterialFields";
import { PlaceholderFields } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/PlaceholderFields";
import {
  duplicateTliData,
  LineType,
  PublishedTakeoffBanner,
  saveAddEditItem,
  TakeoffAiProposals,
  toAddEditItemInput,
  useAddEditItemModalFormState,
  useTakeoffRemovalsEnabled,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/utils";
import { DeleteItemsConfirmationModal } from "src/routes/libraries/plan-package/takeoffs/components/DeleteItemsConfirmationModal";
import {
  useTakeoffsManagerContext,
  useTakeoffsStore,
} from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { queryResult } from "src/utils";

type TakeoffLinePanelProps = { mode: SingleLinePanelMode };

/** Edits a line, or adds one: empty, or pre-filled by Duplicate. */
export function TakeoffLinePanel(props: TakeoffLinePanelProps) {
  const { mode } = props;
  if (mode.kind === "add") return <TakeoffLinePanelView mode={mode} type={mode.type} />;
  return <ExistingLinePanel mode={mode} />;
}

type ExistingLinePanelProps = { mode: Exclude<SingleLinePanelMode, { kind: "add" }> };

/** Loads the line for the version on screen, so a save that moves to a new draft re-loads it from there. */
function ExistingLinePanel(props: ExistingLinePanelProps) {
  const { mode } = props;
  const versionId = useTakeoffsStore((state) => state.planPackage.version.id);
  const query = useAddEditItemModal_GetTliQuery({ variables: { id: mode.tliId, versionId } });
  return queryResult(query, ({ takeoffLineItem }) => (
    <TakeoffLinePanelView mode={mode} type={lineTypeOf(takeoffLineItem)} tli={takeoffLineItem} />
  ));
}

type TakeoffLinePanelViewProps = { mode: SingleLinePanelMode; type: LineType; tli?: AddEditItemModal_TliFragment };

function TakeoffLinePanelView(props: TakeoffLinePanelViewProps) {
  const { mode, type, tli } = props;
  const store = useTakeoffsManagerContext();
  const deleteLines = useDeleteTakeoffLines();
  const planPackageId = useTakeoffsStore((state) => state.planPackage.id);
  const readOnly = !useTakeoffsStore((state) => state.planPackage.canEditLineItems.allowed);
  const takeoffRemovalsEnabled = useTakeoffRemovalsEnabled();
  const { openEdit, registerShownLine, lineDeleted } = useTakeoffsSidePanel();
  const { triggerNotice } = useSnackbar();
  const { closeRightPane } = useRightPaneActions();
  const { openModal } = useModal();
  const resolve = useResolveTakeoffProposals();
  // Memoized so form-state only re-maps the line when the query returns a new one
  const initialTliData = useMemo(
    () => tli && (mode.kind === "duplicate" ? duplicateTliData(tli) : tli),
    [tli, mode.kind],
  );
  const formState = useAddEditItemModalFormState(type, readOnly, initialTliData);

  // Proposals can only be reviewed on a draft version, so a `readOnly` (published) one skips them
  const { proposalsByTliId } = useTakeoffProposals();
  const aep = mode.kind === "edit" && !readOnly ? proposalsByTliId.get(mode.tliId) : undefined;
  // We define our own form state for the accept/ignore statuses instead of adding them to the line's form, since
  // `toAddEditItemInput` sends everything that changed in that form as a TLI field
  const proposalForm = useFormState({
    config: aiProposalsConfig,
    init: {
      input: aep,
      map: (aep) => ({ fieldProposals: flattenEntityProposals([aep]) }),
      ifUndefined: { fieldProposals: [] },
    },
  });
  // `primaryAction` is a plain button rather than a `SubmitButton`, so Add and Save are disabled from these
  const { valid, dirty } = useComputed(
    () => ({ valid: formState.valid, dirty: formState.dirty || proposalForm.dirty }),
    [formState, proposalForm],
  );
  const isAiCreated = aep?.type.code === AiEntityProposalType.Create;
  const aiProposals = useComputed((): TakeoffAiProposals => {
    const proposalFor = (fieldName: string) => findAiFieldProposal(proposalForm.fieldProposals.rows, fieldName);
    return {
      quantity: proposalFor("quantity"),
      unitOfMeasure: proposalFor("unitOfMeasure"),
      task: proposalFor("task"),
      // The fields show these as the line's saved values, which only holds for a line the AI created. The AI never
      // proposes changing an existing line's location or material, since they're how it matches a line to its own
      ...(isAiCreated && { location: proposalFor("location"), materialVariant: proposalFor("materialVariant") }),
    };
  }, [proposalForm, isAiCreated]);
  const { hasPendingProposals, shownCount } = useComputed(() => {
    const pending = proposalForm.fieldProposals.rows.filter(
      (row) => row.status.value === AiFieldProposalStatus.Pending,
    );
    // TODO: Beam gap (DS-453): `MultiSelectField`'s pill list only shows `values`, not `proposedValues`, so Options
    //  can't show as the AI's. The banner only counts what does
    return {
      hasPendingProposals: pending.nonEmpty,
      shownCount: pending.count((row) => row.fieldName.value !== "options"),
    };
  }, [proposalForm]);

  // Registered by the mounted panel, not on open, since Beam's discard prompt can cancel an open. Highlights this
  // line's row, and closes the panel when the line is deleted, from here or from its row
  const editedTliId = mode.kind === "edit" ? mode.tliId : undefined;
  useEffect(() => {
    if (!editedTliId) return;
    return registerShownLine(editedTliId, () => {
      // Prevent the unsaved-changes prompt by reverting first, since the line is gone
      formState.revertChanges();
      proposalForm.revertChanges();
      closeRightPane();
    });
  }, [editedTliId, registerShownLine, formState, proposalForm, closeRightPane]);

  const { label, icon } = lineTypeDetails[type];
  const isNew = mode.kind !== "edit";

  async function save() {
    // Proposals are only on existing lines, so there's no new line to reveal and open like an add has
    if (aep) return saveReview(aep);
    const { saveTli, itemTableApi } = store.getState();
    const result = await saveAddEditItem({ formState, saveTli, isPlaceholderItem: type === "placeholder" });
    const { takeoffLineItems = [], groupTotals = [], version } = result.data?.saveTakeoffLineItems ?? {};
    const added = takeoffLineItems.first;
    // Edits stay on their line; an add moves to the new one
    if (!isNew || !added || !version) return;
    // Expand the new line's group if collapsed. `isCollapsedRow` throws on groups without a row, e.g. the page total
    const groupRowIds = itemTableApi?.getVisibleRows("groupBy").map((row) => row.id) ?? [];
    groupTotals
      .filter((gt) => gt.tliIdsInGroup.includes(added.id) && groupRowIds.includes(gt.groupId))
      .filter((gt) => itemTableApi?.isCollapsedRow(gt.groupId))
      .forEach((gt) => itemTableApi?.toggleCollapsedRow(gt.groupId));
    openEdit(added.id, version.id);
  }

  /** Saves the AI's values the user left alone along with their own edits, and records what they decided. */
  async function saveReview(aep: AiFormAepFragment) {
    const rows = proposalForm.fieldProposals.rows;
    // Ignore All on an AI-created line ignores the whole line, like the table's Ignore All. If the user edited it, save their edits instead
    if (entityIsRemoved(aep.type.code, rows) && !formState.dirty) return ignoreLine(aep);
    const withStatus = (status: AiFieldProposalStatus) => rows.filter((row) => row.status.value === status);
    const pendingNames = withStatus(AiFieldProposalStatus.Pending).map((row) => row.fieldName.value);
    const pendingProposals = aep.fieldProposals.filter((fp) => pendingNames.includes(fp.fieldName));
    const line = {
      ...toTliInput({ ...aep, fieldProposals: pendingProposals }, AiFieldProposalStatus.Accepted),
      ...toAddEditItemInput(formState, type === "placeholder"),
    };
    // One save writes the line and resolves its proposals, rejecting the ones the user changed or ignored
    const rejectFields = withStatus(AiFieldProposalStatus.Rejected).map((row) => row.fieldName.value);
    const result = await resolve([aep], AiFieldProposalStatus.Accepted, { lines: [line], rejectFields });
    const saved = result.data?.saveTakeoffLineItems.takeoffLineItems.first;
    if (saved) formState.update(saved);
    proposalForm.commitChanges();
  }

  /** Rejecting a created line's proposals deactivates it, leaving a REMOVED row, so the panel closes. */
  async function ignoreLine(aep: AiFormAepFragment) {
    await resolve([aep], AiFieldProposalStatus.Rejected);
    triggerNotice({ message: "AI-generated line ignored", icon: "success" });
    lineDeleted(aep.entity.id);
  }

  function createActions(): Pick<RightPanePanelProps, "deleteAction" | "secondaryAction" | "primaryAction"> {
    if (readOnly) return { primaryAction: { label: "Close", onClick: closeRightPane } };
    const secondaryAction = { label: "Cancel", onClick: closeRightPane };
    if (isNew) {
      return {
        secondaryAction,
        primaryAction: { label: "Add", disabled: !valid && "Fill in the required fields", onClick: save },
      };
    }
    return {
      deleteAction: {
        onClick: () =>
          openModal({
            content: (
              <DeleteItemsConfirmationModal
                itemIdsToDelete={[mode.tliId]}
                deleteLines={deleteLines}
                onDeleted={() => lineDeleted(mode.tliId)}
              />
            ),
          }),
      },
      secondaryAction,
      primaryAction: hasPendingProposals
        ? // Saving accepts what's still pending, so there's something to save without an edit
          { label: "Save AI Changes", variant: "ai", disabled: !valid, onClick: save }
        : { label: "Save", disabled: !dirty || !valid, onClick: save },
    };
  }

  const fieldProps = {
    store,
    formState,
    planPackageId,
    initialTliData,
    readOnly,
    takeoffRemovalsEnabled,
    inPanel: true,
    aiProposals,
  };

  return (
    <RightPanePanel
      tag={{ icon, text: label }}
      title={isNew ? `Add ${label}` : (initialTliData?.name ?? "")}
      isDirty={() => formState.dirty || proposalForm.dirty}
      {...createActions()}
    >
      {shownCount > 0 && (
        <RightPaneBanner>
          <AiSlimBanner
            title={`Review ${shownCount} Suggested Change${shownCount === 1 ? "" : "s"}`}
            action={{
              label: "Ignore All",
              onClick: () =>
                resolvePendingAiProposals(proposalForm.fieldProposals.rows, AiFieldProposalStatus.Rejected),
            }}
          />
        </RightPaneBanner>
      )}
      <div css={Css.df.fdc.gap2.$}>
        {readOnly && <PublishedTakeoffBanner />}
        {type === "labor" ? (
          <LaborFields {...fieldProps} />
        ) : type === "material" ? (
          <MaterialFields {...fieldProps} />
        ) : (
          <PlaceholderFields {...fieldProps} isEditing={!isNew} />
        )}
      </div>
    </RightPanePanel>
  );
}
